/**
 * Dashboard Page
 * Landing page for authenticated studio users with real-time data
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCheckout } from '../../context/CheckoutContext';
import { getDashboard, type DashboardData } from '../../api/studios';
import { formatPrice } from '../../api/events';
import { StatsCard, UpcomingClassesCard, PendingActionsCard, DashboardMobileNav } from '../../components/dashboard';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { getCartSummary } = useCheckout();
  const cartSummary = getCartSummary();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Email verification resend state (SDA-161/162 secondary fix — dead button)
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationResendSuccess, setVerificationResendSuccess] = useState(false);
  const [verificationResendError, setVerificationResendError] = useState<string | null>(null);

  const handleResendVerification = async () => {
    if (!user?.email || isResendingVerification) return;
    try {
      setIsResendingVerification(true);
      setVerificationResendError(null);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      if (!response.ok) throw new Error('Failed to resend verification email');
      setVerificationResendSuccess(true);
    } catch (err) {
      setVerificationResendError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setIsResendingVerification(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getDashboard();
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };


  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/dashboard"
                className="text-primary-600 font-medium text-sm"
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/bookings"
                className="text-warm-600 hover:text-warm-900 text-sm"
              >
                My Bookings
              </Link>
              <Link
                to="/dashboard/inquiries"
                className="text-warm-600 hover:text-warm-900 text-sm"
              >
                My Inquiries
              </Link>
              <Link
                to="/dashboard/profile"
                className="text-warm-600 hover:text-warm-900 text-sm"
              >
                Studio Profile
              </Link>
              <Link
                to="/dashboard/contacts"
                className="text-warm-600 hover:text-warm-900 text-sm"
              >
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <Link
                to="/checkout"
                className="relative flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-warm-100 transition-colors group"
              >
                <svg
                  className="h-6 w-6 text-warm-600 group-hover:text-warm-900 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {cartSummary.itemCount > 0 && (
                  <>
                    <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-primary-500 text-white text-xs font-bold">
                      {cartSummary.itemCount}
                    </span>
                    <span className="hidden sm:block text-sm font-medium text-warm-700 group-hover:text-warm-900">
                      Cart
                    </span>
                  </>
                )}
                {cartSummary.itemCount === 0 && (
                  <span className="hidden sm:block text-sm text-warm-500">Cart</span>
                )}
              </Link>
              <span className="text-warm-600 text-sm hidden sm:block">
                {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
            Welcome back{dashboardData?.studio ? `, ${dashboardData.studio.name}` : ''}!
          </h1>
          <p className="text-warm-600 hidden sm:block">
            Manage your masterclass bookings and studio profile.
          </p>
        </div>

        {/* Quick Actions (mobile: Find Classes CTA, desktop: full grid) */}
        <Link
          to="/events"
          className="md:hidden flex items-center gap-4 p-4 mb-6 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl text-white active:scale-[0.98] transition-transform"
        >
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base">Find Classes</p>
            <p className="text-sm text-white/80">Search for Broadway classes near you</p>
          </div>
          <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>

        <div className="hidden md:grid lg:grid-cols-5 md:grid-cols-3 gap-4 mb-6 sm:mb-8">
          <Link
            to="/events"
            className="p-4 sm:p-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg text-white hover:from-primary-600 hover:to-primary-700 transition-all group"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 opacity-90 group-hover:opacity-100"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h4 className="font-semibold mb-1">Find Classes</h4>
            <p className="text-sm opacity-80">Search for Broadway classes near you</p>
          </Link>

          <Link
            to="/dashboard/bookings"
            className="p-4 sm:p-6 bg-white border border-warm-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 text-warm-400 group-hover:text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h4 className="font-semibold text-warm-900 mb-1">My Bookings</h4>
            <p className="text-sm text-warm-500">View and manage your bookings</p>
          </Link>

          <Link
            to="/dashboard/inquiries"
            className="p-4 sm:p-6 bg-white border border-warm-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 text-warm-400 group-hover:text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h4 className="font-semibold text-warm-900 mb-1">My Inquiries</h4>
            <p className="text-sm text-warm-500">Track your inquiry requests</p>
          </Link>

          <Link
            to="/dashboard/profile"
            className="p-4 sm:p-6 bg-white border border-warm-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 text-warm-400 group-hover:text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h4 className="font-semibold text-warm-900 mb-1">Studio Profile</h4>
            <p className="text-sm text-warm-500">Update your studio information</p>
          </Link>

          <Link
            to="/dashboard/contacts"
            className="p-4 sm:p-6 bg-white border border-warm-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3 text-warm-400 group-hover:text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h4 className="font-semibold text-warm-900 mb-1">Contacts</h4>
            <p className="text-sm text-warm-500">Manage studio contacts</p>
          </Link>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-danger-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-danger-700">{error}</span>
              <button
                onClick={loadDashboard}
                className="ml-auto text-sm font-medium text-danger-600 hover:text-danger-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatsCard
            title="Upcoming Classes"
            value={isLoading ? '...' : dashboardData?.stats.upcoming_classes || 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
            variant="primary"
          />

          <StatsCard
            title="Total Bookings"
            value={isLoading ? '...' : dashboardData?.stats.total_bookings || 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            }
          />

          <StatsCard
            title="Pending Payments"
            value={
              isLoading
                ? '...'
                : formatPrice((dashboardData?.stats.pending_payments || 0) * 100)
            }
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            variant={dashboardData?.stats.pending_payments ? 'warning' : 'default'}
          />

          <StatsCard
            title="Completed Classes"
            value={isLoading ? '...' : dashboardData?.stats.completed_classes || 0}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            variant="success"
          />
        </div>

        {/* Main Grid - Classes and Actions */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Upcoming Classes - Takes 2 columns */}
          <div className="lg:col-span-2">
            <UpcomingClassesCard
              classes={dashboardData?.upcoming_classes || []}
              isLoading={isLoading}
            />
          </div>

          {/* Pending Actions */}
          <div>
            <PendingActionsCard
              actions={dashboardData?.pending_actions || []}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Profile Status - Only show if email not verified */}
        {!user?.email_verified && (
          <div className="mt-8 p-3 sm:p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-warning-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-warning-800">
                  Please verify your email address
                </p>
                <p className="text-sm text-warning-700">
                  Check your inbox for the verification link to complete your account setup.
                </p>
                {verificationResendSuccess && (
                  <p className="text-sm text-success-700 mt-1">
                    Verification email sent — check your inbox.
                  </p>
                )}
                {verificationResendError && (
                  <p className="text-sm text-danger-700 mt-1">{verificationResendError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={isResendingVerification || verificationResendSuccess}
                className="text-sm font-medium text-warning-700 hover:text-warning-800 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResendingVerification ? 'Sending...' : verificationResendSuccess ? 'Sent' : 'Resend Email'}
              </button>
            </div>
          </div>
        )}
      </main>

    </div>
  );
}
